import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

/**
 * MobileTabDropdown - Settings page mobile navigation
 * 
 * Displays a dropdown selector for switching between settings tabs on mobile.
 * Includes a visual separator below to distinguish from page content.
 */
const MobileTabDropdown = ({ groups, activeTab, onTabChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Find the current tab info
  const getCurrentTab = () => {
    for (const group of groups) {
      const tab = group.tabs.find(t => t.id === activeTab);
      if (tab) {
        return { tab, group };
      }
    }
    return null;
  };

  const current = getCurrentTab();
  const CurrentIcon = current?.tab?.icon;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (tabId) => {
    onTabChange(tabId);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-xl shadow-sm text-left focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
      >
        <div className="flex items-center gap-3">
          {CurrentIcon && <CurrentIcon size={20} className="text-primary-600" />}
          <div>
            <p className="font-semibold text-gray-900">{current?.tab?.label || 'Select'}</p>
            <p className="text-xs text-gray-500">{current?.group?.label}</p>
          </div>
        </div>
        <ChevronDown 
          size={20} 
          className={`text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          {groups.map((group, groupIndex) => (
            <div key={group.id}>
              {/* Group Header */}
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  {group.label}
                </p>
              </div>
              
              {/* Group Tabs */}
              {group.tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = tab.id === activeTab;
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleSelect(tab.id)}
                    className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
                      isActive 
                        ? 'bg-primary-50 text-primary-700' 
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon size={18} className={isActive ? 'text-primary-600' : 'text-gray-400'} />
                      <span className="font-medium">{tab.label}</span>
                    </div>
                    {isActive && <Check size={18} className="text-primary-600" />}
                  </button>
                );
              })}
              
              {/* Divider between groups (except last) */}
              {groupIndex < groups.length - 1 && (
                <div className="border-b border-gray-100" />
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* ============================================ */}
      {/* VISUAL SEPARATOR - Distinguishes nav from content */}
      {/* ============================================ */}
      <div className="mt-4 mb-2 flex items-center gap-3">
        <div className="flex-1 h-px bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200"></div>
        <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider px-2">
          {current?.tab?.label}
        </span>
        <div className="flex-1 h-px bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200"></div>
      </div>
    </div>
  );
};

export default MobileTabDropdown;